/**
 * `/v1/securities/:ticker/series` in local mode (plan §6.6).
 *
 * The cache is INJECTED rather than module-global: `createSeriesCache()` is the
 * one sanctioned mutable structure in `core` (ADR-0004), and a module-level
 * instance would be process-global state that outlives any repository and makes
 * two independently-constructed adapters share a memo. The composer owns exactly
 * one instance and passes it here.
 *
 * The payload is the COMPLETE bar set for the window. Decimating for rendering
 * is the chart's business; a decimated payload would make the local adapter's
 * bar count differ from the live route's for no reason a caller could see.
 */
import type { PriceSeries, SeriesQuery, Ticker } from '@meridian/contracts';

import { DatasetNotFoundError } from '../../ports/dataset-repository';
import { generateSeries } from '../../series/generate-series';
import { syntheticSessions } from '../../series/synthetic-sessions';

import type { SeriesCache } from '../../series/series-cache';
import type { DerivedSecurity } from '../../universe/derive-security';

export function resolveSecurity(
  byTicker: ReadonlyMap<Ticker, DerivedSecurity>,
  ticker: Ticker,
): DerivedSecurity {
  const found = byTicker.get(ticker);
  if (found === undefined) throw new DatasetNotFoundError('security', ticker);
  return found;
}

export function buildSeries(
  security: DerivedSecurity,
  cache: SeriesCache,
  query: SeriesQuery,
): PriceSeries {
  const bars = [...generateSeries(security, query.window, cache)];
  return {
    ticker: security.ticker,
    window: query.window,
    /* Copied out of the frozen cached array: PriceSeriesSchema types `bars` as
       mutable, and handing back the cached instance would let one caller's
       in-place sort corrupt every later read of the same key. */
    bars,
    sessions: [...syntheticSessions(bars.length)],
  };
}
